import React, { useEffect, useState } from 'react';
import { ConfigProvider } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import { StyledDateRangePicker, PickerContainer } from './styles';
import { DateRangePickerProps } from './types';
import { useTheme } from '../../context/ThemeContext';
import { getAntdThemeConfig } from '../../utils/antdTheme';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  selectedDateRange,
  onDateRangeChange,
  placeholder = 'Select date range',
  size = 'middle',
  style,
  className,
}) => {
  const { isDarkMode } = useTheme();
  const [dates, setDates] = useState<[Dayjs | null, Dayjs | null] | null>(null);

  useEffect(() => {
    if (selectedDateRange?.startDate && selectedDateRange?.endDate) {
      let startDate: Date;
      let endDate: Date;
      
      if (moment.isMoment(selectedDateRange.startDate)) {
        startDate = selectedDateRange.startDate.toDate();
      } else if (selectedDateRange.startDate instanceof Date) {
        startDate = selectedDateRange.startDate;
      } else {
        startDate = new Date(selectedDateRange.startDate);
      }
      
      if (moment.isMoment(selectedDateRange.endDate)) {
        endDate = selectedDateRange.endDate.toDate();
      } else if (selectedDateRange.endDate instanceof Date) {
        endDate = selectedDateRange.endDate;
      } else {
        endDate = new Date(selectedDateRange.endDate);
      }
      
      const start = dayjs(startDate);
      const end = dayjs(endDate);
      setDates([start, end]);
    } else {
      setDates(null);
    }
  }, [selectedDateRange]);

  const handleChange = (values: [Dayjs | null, Dayjs | null] | null) => {
    if (values && values[0] && values[1]) {
      const startDate = values[0].toDate();
      const endDate = values[1].toDate();
      onDateRangeChange({
        startDate: moment(startDate),
        endDate: moment(endDate),
      });
    } else {
      onDateRangeChange({
        startDate: null,
        endDate: null,
      });
    }
  };

  const antdTheme = getAntdThemeConfig(isDarkMode);

  return (
    <ConfigProvider theme={antdTheme}>
      <PickerContainer className={className} style={style}>
        <StyledDateRangePicker
          value={dates}
          onChange={handleChange}
          format="MMM DD, YYYY"
          placeholder={[placeholder, placeholder]}
          size={size}
          suffixIcon={<FontAwesomeIcon icon={faCalendarAlt} />}
          allowClear
        />
      </PickerContainer>
    </ConfigProvider>
  );
};

export default DateRangePicker;
